/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type { BlockGroup } from '../suggestion-editor';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';

export const SPACING_BETWEEN_CARDS = 16;

type SuggestionCardLayoutDetails = {
  blockGroupId: string;
  cardId: string;
  idealTop: number;
  cardElement: HTMLElement;
  cardHeight: number;
};

/**
 * Position suggestion cards so that each is aligned with its block group, but if any would overlap,
 * move them down to avoid overlap, cascading as needed.
 */
export function positionSuggestionCardsWithoutOverlap(
  editedBlockGroups: BlockGroup[],
  focusedBlockGroup?: BlockGroup,
) {
  let container = document.getElementById('suggestion-editor-content');
  if (!container) {
    return;
  }

  // Do initial positioning
  positionCards(container, editedBlockGroups, focusedBlockGroup);

  // Setup repositioning after all images load
  setupImageLoadHandler(container, () => {
    positionCards(container, editedBlockGroups, focusedBlockGroup);
  });
}

export function hasUnloadedImages(container: HTMLElement) {
  let images = Array.from(container.getElementsByTagName('img'));
  return images.some((img) => !img.complete);
}

/**
 * Sets up a handler to call the callback once all unloaded images in the container have loaded.
 * If all images are already loaded, returns immediately without setting up handlers.
 */
export function setupImageLoadHandler(container: HTMLElement, onAllImagesLoaded: () => void) {
  // Find all unloaded images
  let images = Array.from(container.getElementsByTagName('img'));
  let unloadedImages = images.filter((img) => !img.complete);

  if (unloadedImages.length === 0) {
    return;
  }

  // Keep track of loaded images
  let loadedCount = 0;
  let handleLoad = () => {
    loadedCount++;
    if (loadedCount === unloadedImages.length) {
      // All images have loaded, do final positioning
      onAllImagesLoaded();
      // Cleanup listeners
      unloadedImages.forEach((img) => {
        img.removeEventListener('load', handleLoad);
        img.removeEventListener('error', handleLoad);
      });
    }
  };

  // Add listeners to all unloaded images
  unloadedImages.forEach((img) => {
    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleLoad);
  });
}

function positionCards(
  container: HTMLElement | null,
  editedBlockGroups: BlockGroup[],
  focusedBlockGroup?: BlockGroup,
) {
  if (!container) {
    return;
  }

  let containerRect = container.getBoundingClientRect();
  let cards = gatherCardDetails(containerRect, editedBlockGroups);

  if (focusedBlockGroup) {
    applyCardPositionsWithFocusedBlockGroup(focusedBlockGroup, cards);
  } else {
    // No focused card, cascade all from the top
    let lastCardBottom = 0;
    for (let i = 0; i < cards.length; i++) {
      let { idealTop, cardElement, cardHeight } = cards[i];
      let top = i === 0 ? idealTop : Math.max(idealTop, lastCardBottom + SPACING_BETWEEN_CARDS);
      cardElement.style.top = `${top}px`;
      lastCardBottom = top + cardHeight;
    }
  }
}

function applyCardPositionsWithFocusedBlockGroup(
  focusedBlockGroup: BlockGroup,
  cards: SuggestionCardLayoutDetails[],
) {
  // Find the focused card index
  let focusedCardIndex = cards.findIndex((c) => c.blockGroupId === focusedBlockGroup.id);
  if (focusedCardIndex === -1) {
    return;
  }
  // Pin the focused card
  let focusedCard = cards[focusedCardIndex];
  focusedCard.cardElement.style.top = `${focusedCard.idealTop}px`;
  // Cascade cards above
  let prevBottom = focusedCard.idealTop;
  for (let i = focusedCardIndex - 1; i >= 0; i--) {
    let card = cards[i];
    // Place as close as possible to its ideal, but not overlapping the card below
    let maxBottom = prevBottom - SPACING_BETWEEN_CARDS;
    let top = Math.min(card.idealTop, maxBottom - card.cardHeight);
    card.cardElement.style.top = `${top}px`;
    prevBottom = top;
  }
  // Cascade cards below
  let lastCardBottom = focusedCard.idealTop + focusedCard.cardHeight;
  for (let i = focusedCardIndex + 1; i < cards.length; i++) {
    let card = cards[i];
    // Place as close as possible to its ideal, but not overlapping the card above
    let minTop = lastCardBottom + SPACING_BETWEEN_CARDS;
    let top = Math.max(card.idealTop, minTop);
    card.cardElement.style.top = `${top}px`;
    lastCardBottom = top + card.cardHeight;
  }
}

function gatherCardDetails(
  containerRect: DOMRect,
  editedBlockGroups: BlockGroup[],
): SuggestionCardLayoutDetails[] {
  // Gather all cards and their block group positions
  let cards: SuggestionCardLayoutDetails[] = [];
  for (let i = 0; i < editedBlockGroups.length; i++) {
    let blockGroup = editedBlockGroups[i];
    let cardNumber = editedBlockGroupCardNumber(editedBlockGroups, blockGroup);
    let cardId = `suggestion-card-${cardNumber}`;
    let blockGroupElement = document.getElementById(blockGroup.id);
    let cardElement = document.getElementById(cardId);
    if (blockGroupElement && cardElement) {
      let blockGroupRect = blockGroupElement.getBoundingClientRect();
      let idealTop = blockGroupRect.top - containerRect.top;

      let isMiniVersion = cardElement.classList.contains('suggestion-card-wrapper-small');
      let cardHeight = isMiniVersion ? 210 : cardElement.offsetHeight; //210 is set height of mini version

      cards.push({ blockGroupId: blockGroup.id, cardId, idealTop, cardElement, cardHeight });
    }
  }
  // Sort by idealTop (top to bottom)
  return cards.sort((a, b) => a.idealTop - b.idealTop);
}

export function editedBlockGroupCardNumber(
  editedBlockGroups: BlockGroup[],
  blockGroup: BlockGroup,
) {
  let pendingEditedBlockGroups = editedBlockGroups.filter(
    (group) => group.reviewRequest?.get('status') === Status.Pending,
  );
  return pendingEditedBlockGroups.findIndex((group) => group.id === blockGroup.id) + 1;
}
